import { AlertTriangle, RefreshCw, WifiOff, Clock, ShieldAlert } from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './card';
import { cn } from '@/lib/utils';

export type ErrorType =
  'network' | 'timeout' | 'unauthorized' | 'forbidden' | 'validation' | 'server' | 'unknown';

export interface ErrorDisplayProps {
  error: Error | unknown;
  title?: string;
  description?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  showDetails?: boolean;
}

function getErrorType(error: unknown): ErrorType {
  if (!(error instanceof Error)) {
    return 'unknown';
  }

  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();

  if (message.includes('network') || message.includes('fetch') || message.includes('offline')) {
    return 'network';
  }
  if (message.includes('timeout') || name.includes('timeout')) {
    return 'timeout';
  }
  if (message.includes('401') || message.includes('unauthorized')) {
    return 'unauthorized';
  }
  if (message.includes('403') || message.includes('forbidden')) {
    return 'forbidden';
  }
  if (name === 'zoderror' || message.includes('validation')) {
    return 'validation';
  }
  if (message.includes('500') || message.includes('server error')) {
    return 'server';
  }

  return 'unknown';
}

function getErrorIcon(type: ErrorType) {
  switch (type) {
    case 'network':
      return <WifiOff className="h-5 w-5 text-destructive" />;
    case 'timeout':
      return <Clock className="h-5 w-5 text-destructive" />;
    case 'unauthorized':
    case 'forbidden':
      return <ShieldAlert className="h-5 w-5 text-destructive" />;
    default:
      return <AlertTriangle className="h-5 w-5 text-destructive" />;
  }
}

function getDefaultTitle(type: ErrorType): string {
  switch (type) {
    case 'network':
      return 'Network Error';
    case 'timeout':
      return 'Request Timeout';
    case 'unauthorized':
      return 'Authentication Required';
    case 'forbidden':
      return 'Access Denied';
    case 'validation':
      return 'Validation Error';
    case 'server':
      return 'Server Error';
    default:
      return 'An Error Occurred';
  }
}

function getDefaultDescription(type: ErrorType): string {
  switch (type) {
    case 'network':
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    case 'timeout':
      return 'The request took too long to complete. Please try again.';
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'forbidden':
      return "You don't have permission to perform this action.";
    case 'validation':
      return 'Please check your input and try again.';
    case 'server':
      return 'The server encountered an error. Please try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

export function ErrorDisplay({
  error,
  title,
  description,
  onRetry,
  retryLabel = 'Retry',
  className,
  showDetails = false,
}: ErrorDisplayProps) {
  const errorType = getErrorType(error);
  const errorMessage = error instanceof Error ? error.message : String(error);
  const errorTitle = title || getDefaultTitle(errorType);
  const errorDescription = description || getDefaultDescription(errorType);

  return (
    <Card className={cn('border-destructive/50', className)}>
      <CardHeader>
        <div className="flex items-center gap-2">
          {getErrorIcon(errorType)}
          <CardTitle>{errorTitle}</CardTitle>
        </div>
        <CardDescription>{errorDescription}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {showDetails && errorMessage && (
          <div className="rounded-md bg-muted p-3">
            <p className="text-xs font-medium mb-1">Error Details:</p>
            <pre className="text-xs overflow-auto text-muted-foreground whitespace-pre-wrap break-words">
              {errorMessage}
            </pre>
          </div>
        )}
        {onRetry && (
          <Button onClick={onRetry} variant="default" className="w-full sm:w-auto">
            <RefreshCw className="mr-2 h-4 w-4" />
            {retryLabel}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
